/**
 * The rail's hover flyout: resting the pointer on a closed token lists its
 * pages in a panel beside the rail, so any page is one hover and one click
 * away without the rail moving. Timing is lib/flyout. Only on screens with a
 * real hover and a fine pointer, and not in the phone drawer: keyboard and
 * touch keep the tree. Picking a page navigates; the rail then opens that
 * token with the page marked (TokenTree), so nothing here writes rail state.
 *
 * The panel is portalled to <body>, so the rail's overflow cannot clip it.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router";
import { fmtPrice } from "@/lib/format";
import { flyoutTimer } from "@/lib/flyout";
import { syncState } from "@/lib/jobs";
import { TOKEN_SECTIONS } from "@/lib/sections";
import { useUi } from "@/state/ui";
import { RailFlyoutContext, type FlyoutTarget, type RailFlyout } from "./railFlyoutContext";
import { linkCls } from "./railStyles";

const HOVER_QUERY = "(hover: hover) and (pointer: fine)";

function subscribeHover(onChange: () => void) {
  const mq = window.matchMedia?.(HOVER_QUERY);
  mq?.addEventListener("change", onChange);
  return () => mq?.removeEventListener("change", onChange);
}
const canHover = () => window.matchMedia?.(HOVER_QUERY).matches ?? false;

/** Wraps the rail's nav; renders the panel for whichever token is hovered. */
export function RailFlyoutProvider({ children }: { children: ReactNode }) {
  const narrow = useUi((s) => s.narrow);
  const hover = useSyncExternalStore(subscribeHover, canHover, () => false);
  const [shown, setShown] = useState<FlyoutTarget | null>(null);
  const [fly] = useState(() => flyoutTimer<FlyoutTarget>(setShown));

  // Escape closes it; so does scrolling, which would leave it beside the wrong row
  useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") fly.close();
    };
    const onScroll = () => fly.close();
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [shown, fly]);
  useEffect(() => () => fly.close(), [fly]);

  const value: RailFlyout = {
    enabled: hover && !narrow,
    shownId: shown?.job.id ?? null,
    enter: (target) => fly.enterRow(target.job.id, target),
    leave: fly.leave,
    close: fly.close,
  };
  return (
    <RailFlyoutContext.Provider value={value}>
      {children}
      {shown &&
        value.enabled &&
        createPortal(
          <FlyoutPanel target={shown} onEnter={fly.enterPanel} onLeave={fly.leave} onPick={fly.close} />,
          document.body,
        )}
    </RailFlyoutContext.Provider>
  );
}

function FlyoutPanel({
  target,
  onEnter,
  onLeave,
  onPick,
}: {
  target: FlyoutTarget;
  onEnter: () => void;
  onLeave: () => void;
  onPick: () => void;
}) {
  const { job, defaultView, row } = target;
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const sync = syncState(job.tracking);

  // beside the rail, aligned to the row, kept inside the viewport
  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 0;
    const want = row.getBoundingClientRect().top - 5;
    setAt({
      left: (row.closest("nav") ?? row).getBoundingClientRect().right + 4,
      top: Math.max(8, Math.min(want, window.innerHeight - h - 8)),
    });
  }, [row]);

  return (
    <div
      ref={ref}
      data-rail
      data-testid="rail-flyout"
      role="group"
      aria-label={`${job.symbol} pages`}
      className="fixed z-50 min-w-[196px] rounded-[3px] border border-border-strong bg-bg-elev py-1 text-[11px] shadow-[0_6px_18px_rgba(0,0,0,0.18)] motion-safe:animate-[rail-fly_90ms_ease-out]"
      // placed once measured: until then it is off screen, so it never flashes in the wrong place
      style={at ?? { left: -9999, top: 0 }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a")) onPick();
      }}
    >
      <div className="mb-0.5 flex items-baseline gap-1.5 border-b border-border px-2 pt-0.5 pb-1 whitespace-nowrap">
        <span className="font-bold text-fg">{job.symbol}</span>
        <span className="text-fg-faint">
          {job.chain} · {job.token.slice(-4)}
        </span>
        {sync.syncing ? (
          job.mark && <span className="num ml-auto pl-2.5 text-fg-muted">{fmtPrice(job.mark.price_usd)}</span>
        ) : (
          <span className={`ml-auto pl-2.5 ${sync.tone === "danger" ? "text-danger" : "text-warn"}`}>
            {sync.label}
          </span>
        )}
      </div>
      {defaultView && (
        <Link to={defaultView.url} className={linkCls({ isActive: false })} title={defaultView.url}>
          <span className="min-w-0 truncate">{defaultView.name}</span>
          <span className="flex-none text-fg-faint">· default</span>
        </Link>
      )}
      {TOKEN_SECTIONS.map((s) => (
        <NavLink key={s.id} to={s.path(job.chain, job.token)} end={s.id === ""} className={linkCls}>
          {s.label}
        </NavLink>
      ))}
      <div className="mt-0.5 border-t border-border px-2 pt-1 text-2xs text-fg-faint">
        click the token to keep it open in the rail
      </div>
    </div>
  );
}
